import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Section } from '../../../../components/ui/Section';
import {
  CatalogueGovernanceClient,
  DuplicateProductRow,
  DuplicateSuggestedAction,
} from '../../../../app/services/api/catalogueGovernance';

const ACTION_KEY: Record<DuplicateSuggestedAction, string> = {
  Merge: 'Merge',
  MarkNotDuplicate: 'MarkNotDuplicate',
  ReplaceInRecipes: 'ReplaceInRecipes',
  BlockDuplicate: 'BlockDuplicate',
  KeepBoth: 'KeepBoth',
};

const DuplicateProductsTab = () => {
  const { t } = useTranslation('catalogueGovernance');
  const [rows, setRows] = useState<DuplicateProductRow[]>([]);

  const load = () => CatalogueGovernanceClient.getDuplicateProducts().then(setRows);

  useEffect(() => {
    load();
  }, []);

  const handleResolve = async (id: string, action: DuplicateSuggestedAction) => {
    await CatalogueGovernanceClient.resolveDuplicate(id, action);
    load();
  };

  return (
    <Section>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('duplicateProducts.columnPrimaryProduct', 'Primary product')}</TableCell>
            <TableCell>{t('duplicateProducts.columnPossibleDuplicate', 'Possible duplicate')}</TableCell>
            <TableCell>{t('duplicateProducts.columnSupplier', 'Supplier')}</TableCell>
            <TableCell>{t('duplicateProducts.columnCatalogue', 'Catalogue')}</TableCell>
            <TableCell align="right">{t('duplicateProducts.columnMatchConfidence', 'Match confidence')}</TableCell>
            <TableCell>{t('duplicateProducts.columnReason', 'Reason')}</TableCell>
            <TableCell align="right">{t('duplicateProducts.columnRecipeUsage', 'Recipe usage')}</TableCell>
            <TableCell align="right">{t('duplicateProducts.columnMenuUsage', 'Menu usage')}</TableCell>
            <TableCell>{t('duplicateProducts.columnSuggestedAction', 'Suggested action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={9}>{t('duplicateProducts.emptyState', 'No duplicate product candidates to show.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.primaryProduct}</TableCell>
              <TableCell>{row.possibleDuplicate}</TableCell>
              <TableCell>{row.supplier}</TableCell>
              <TableCell>{row.catalogue}</TableCell>
              <TableCell align="right">
                <Typography
                  variant="textSM"
                  fontWeight={600}
                  color={row.matchConfidence >= 0.9 ? 'error.main' : row.matchConfidence >= 0.75 ? 'warning.main' : 'text.primary'}
                >
                  {(row.matchConfidence * 100).toFixed(0)}%
                </Typography>
              </TableCell>
              <TableCell>{row.reason}</TableCell>
              <TableCell align="right">{row.recipeUsage}</TableCell>
              <TableCell align="right">{row.menuUsage}</TableCell>
              <TableCell>
                <Stack spacing={1}>
                  <Stack direction="row"
                    spacing={1}
                    flexWrap="wrap">
                    {(Object.keys(ACTION_KEY) as DuplicateSuggestedAction[]).map((action) => (
                      <Button
                        key={action}
                        size="md"
                        variant={row.suggestedAction === action ? 'primary' : 'secondary'}
                        onClick={() => handleResolve(row.id, action)}
                      >
                        {t(`duplicateProducts.action.${ACTION_KEY[action]}`, action)}
                      </Button>
                    ))}
                  </Stack>
                  {row.resolutionNote && (
                    <Typography variant="textXS"
                      color="text.secondary">{row.resolutionNote}</Typography>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
};

export default DuplicateProductsTab;
